import { z } from "zod";
import { billingIntervalSchema, currencySchema, isPaidPlan, paidPlanSchema, planIdSchema, priceListSchema } from "./billing";

/**
 * The platform admin portal (ADR 0009): one read of the whole deployment, for the
 * operator watching it. Counts, names and money; nothing anyone could act on, and
 * nothing a deck or a participant said.
 */

/** Where an organisation stands with its plan, as the gate in contracts/billing.ts sees it. */
export const ADMIN_PLAN_STATES = ["none", "active", "grace", "lapsed"] as const;
export const adminPlanStateSchema = z.enum(ADMIN_PLAN_STATES);
export type AdminPlanState = z.infer<typeof adminPlanStateSchema>;

/** What a platform admin can do, as `admin_actions` records it. */
export const ADMIN_ACTIONS = [
  "suspend",
  "reinstate",
  "set_plan",
  "extend",
  "auto_renew",
  "end_session",
  "block",
  "unblock",
  "sign_out",
  "verify_email",
  "retry_jobs",
  "set_prices",
  "create_user",
  "password_link",
] as const;
export const adminActionKindSchema = z.enum(ADMIN_ACTIONS);
export type AdminActionKind = z.infer<typeof adminActionKindSchema>;

/** One thing a platform admin did, to an organisation, a person, or both. */
export const adminAuditEntrySchema = z.object({
  id: z.string(),
  action: adminActionKindSchema,
  actor: z.string().nullable(),
  actorUserId: z.string(),
  organisation: z.object({ orgId: z.string(), name: z.string() }).nullable(),
  user: z.object({ userId: z.string(), name: z.string() }).nullable(),
  detail: z.record(z.unknown()),
  createdAt: z.string(),
});
export type AdminAuditEntry = z.infer<typeof adminAuditEntrySchema>;

const moneySchema = z.object({ currency: z.string(), amountMinor: z.number().int() });

/** One organisation in a list: the overview's newest, or a search. */
export const adminOrganisationSchema = z.object({
  orgId: z.string(),
  name: z.string(),
  createdAt: z.string(),
  members: z.number().int(),
  plan: planIdSchema.nullable(),
  planState: adminPlanStateSchema,
  periodEnd: z.string().nullable(),
  decks: z.number().int(),
  sessions: z.number().int(),
  participants: z.number().int(),
  lastSessionAt: z.string().nullable(),
  /** Shut out by a platform admin (ADR 0009) until reinstated. */
  suspended: z.boolean(),
});
export type AdminOrganisation = z.infer<typeof adminOrganisationSchema>;

const adminPaymentSchema = z.object({
  id: z.string(),
  organisation: z.string(),
  kind: z.enum(["checkout", "renewal"]),
  plan: planIdSchema.nullable(),
  amountMinor: z.number().int(),
  currency: z.string(),
  status: z.enum(["pending", "succeeded", "failed", "refunded"]),
  payer: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
});

export const adminOverviewSchema = z.object({
  generatedAt: z.string(),
  totals: z.object({
    organisations: z.number().int(),
    organisationsThisWeek: z.number().int(),
    users: z.number().int(),
    usersThisWeek: z.number().int(),
    /** Live sessions in their lobby or running, now. */
    liveNow: z.number().int(),
    /** Self-paced sessions still taking answers. */
    selfPacedOpen: z.number().int(),
    /** Participants in the sessions live now. */
    participantsNow: z.number().int(),
    sessionsThisWeek: z.number().int(),
    participantsToday: z.number().int(),
    participantsThisWeek: z.number().int(),
    answersToday: z.number().int(),
    suspended: z.number().int(),
    /** People blocked from their accounts. */
    blocked: z.number().int(),
  }),
  /** Organisations by plan: `none` has never chosen, `lapsed` must choose again. */
  plans: z.object({
    none: z.number().int(),
    free: z.number().int(),
    starter: z.number().int(),
    pro: z.number().int(),
    business: z.number().int(),
    enterprise: z.number().int(),
    lapsed: z.number().int(),
  }),
  revenue: z.object({
    /** Monthly recurring revenue at list price, per currency (adminRevenueSchema). */
    mrr: z.array(moneySchema),
    /** Settled payments since the first of this month (UTC), per currency. */
    thisMonth: z.array(moneySchema),
    last30Days: z.array(moneySchema),
    failedThisWeek: z.number().int(),
    pending: z.number().int(),
    /** Reminders to pay again that are due and not yet sent: waiting on email, if many. */
    remindersWaiting: z.number().int(),
  }),
  live: z.array(
    z.object({
      sessionId: z.string(),
      organisation: z.string(),
      deck: z.string().nullable(),
      mode: z.enum(["live", "self_paced"]),
      status: z.enum(["lobby", "live"]),
      participants: z.number().int(),
      startedAt: z.string().nullable(),
      closesAt: z.string().nullable(),
    }),
  ),
  organisations: z.array(adminOrganisationSchema),
  payments: z.array(adminPaymentSchema),
  signups: z.array(
    z.object({
      name: z.string(),
      email: z.string(),
      createdAt: z.string(),
      organisations: z.array(z.string()),
    }),
  ),
  system: z.object({
    database: z.boolean(),
    valkey: z.boolean(),
    /** Workers connected to the answer queue right now; 0 means answers are not being saved. */
    workers: z.number().int(),
    /** The answer queue (apps/worker): waiting should drain within seconds; failed needs a look. */
    answerQueue: z.object({
      waiting: z.number().int(),
      active: z.number().int(),
      failed: z.number().int(),
    }),
  }),
});
export type AdminOverview = z.infer<typeof adminOverviewSchema>;

/** Everything about one organisation a platform admin manages it from. */
export const adminOrganisationDetailSchema = z.object({
  organisation: adminOrganisationSchema.extend({ slug: z.string() }),
  subscription: z
    .object({
      autoRenew: z.boolean(),
      currency: z.string().nullable(),
      paymentMethod: z.string().nullable(),
      participantLimit: z.number().int(),
      /** Null for a plan given by hand, which does not renew. */
      interval: billingIntervalSchema.nullable(),
      renewalAttempts: z.number().int(),
    })
    .nullable(),
  suspension: z
    .object({
      reason: z.string(),
      suspendedAt: z.string(),
      /** The admin who did it, as their name; null if their account is gone. */
      by: z.string().nullable(),
    })
    .nullable(),
  members: z.array(
    z.object({
      userId: z.string(),
      name: z.string(),
      email: z.string(),
      role: z.string(),
      joinedAt: z.string(),
    }),
  ),
  /** Invitations to it still waiting to be accepted. */
  invitations: z.array(z.object({ email: z.string(), role: z.string().nullable(), expiresAt: z.string() })),
  sessions: z.array(
    z.object({
      sessionId: z.string(),
      deck: z.string().nullable(),
      mode: z.enum(["live", "self_paced"]),
      status: z.enum(["lobby", "live", "ended"]),
      participants: z.number().int(),
      createdAt: z.string(),
    }),
  ),
  payments: z.array(adminPaymentSchema),
  /** What platform admins have done to it, newest first. */
  actions: z.array(adminAuditEntrySchema),
});
export type AdminOrganisationDetail = z.infer<typeof adminOrganisationDetailSchema>;

// ---------------------------------------------------------------------------
// Lists: every one paged, filtered by its query string, and exported whole as CSV.
// ---------------------------------------------------------------------------

export const ADMIN_PAGE_SIZE = 50;
/** How many rows an export asks for at most: the whole list, for a deployment this size. */
export const ADMIN_EXPORT_LIMIT = 10_000;

/** One page of a list, and how many there are in all. */
export function adminPageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), total: z.number().int(), page: z.number().int(), pageSize: z.number().int() });
}

/** What every list takes: which page, and how long one is (an export asks for all). */
const pagingSchema = {
  page: z.coerce.number().int().min(1).max(100_000).catch(1),
  pageSize: z.coerce.number().int().min(1).max(ADMIN_EXPORT_LIMIT).catch(ADMIN_PAGE_SIZE),
};
/** An optional filter: anything not on the list, an empty string included, is no filter. */
const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values).optional().catch(undefined);
const search = z.string().trim().max(200).catch("").default("");

export const ADMIN_ORG_SORTS = ["newest", "oldest", "name", "participants", "active"] as const;
export const adminOrganisationQuerySchema = z.object({
  q: search,
  plan: optionalEnum(["free", "pro", "business", "enterprise"] as const),
  state: optionalEnum(ADMIN_PLAN_STATES),
  suspended: optionalEnum(["yes", "no"] as const),
  /**
   * Whose sign-ups: none given is only organisations someone in has confirmed their
   * email address, where confirming is required — the rest are almost all bots'.
   */
  accounts: optionalEnum(["unconfirmed", "all"] as const),
  sort: z.enum(ADMIN_ORG_SORTS).catch("newest").default("newest"),
  ...pagingSchema,
});
export type AdminOrganisationQuery = z.infer<typeof adminOrganisationQuerySchema>;
export const adminOrganisationPageSchema = adminPageSchema(adminOrganisationSchema);

/** Organisations on a plan they pay for, or were given: who, until when, and what they are worth. */
export const adminSubscriberSchema = z.object({
  orgId: z.string(),
  name: z.string(),
  plan: planIdSchema,
  planState: adminPlanStateSchema,
  interval: billingIntervalSchema.nullable(),
  periodEnd: z.string().nullable(),
  autoRenew: z.boolean(),
  /** Retries of a declined renewal so far. */
  renewalAttempts: z.number().int(),
  currency: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  /** What the plan brings in a month at list price; null for one given by hand. */
  monthly: moneySchema.nullable(),
  /** Everything it has ever paid, per currency. */
  lifetime: z.array(moneySchema),
  lastPaymentAt: z.string().nullable(),
  since: z.string(),
  suspended: z.boolean(),
});
export type AdminSubscriber = z.infer<typeof adminSubscriberSchema>;
export const adminSubscriberQuerySchema = z.object({
  q: search,
  plan: optionalEnum(["free", "pro", "business", "enterprise"] as const),
  state: optionalEnum(["active", "grace", "lapsed"] as const),
  /** paid: bought and paying; given: put on the plan by an admin. */
  source: optionalEnum(["paid", "given"] as const),
  renews: optionalEnum(["yes", "no"] as const),
  ...pagingSchema,
});
export type AdminSubscriberQuery = z.infer<typeof adminSubscriberQuerySchema>;
export const adminSubscriberPageSchema = adminPageSchema(adminSubscriberSchema).extend({
  /** Monthly recurring revenue of every subscriber matched, not only this page, per currency. */
  mrr: z.array(moneySchema),
});

export const adminRevenueSchema = z.object({
  generatedAt: z.string(),
  /** Monthly recurring revenue: each paying plan's month at list price, a yearly one divided by twelve. */
  mrr: z.array(moneySchema),
  /** Organisations on a paid plan that is running (or in its grace days), by plan. */
  paying: z.object({ starter: z.number().int(), pro: z.number().int(), business: z.number().int(), total: z.number().int() }),
  /** On a plan an admin gave them, not paying. */
  given: z.number().int(),
  lifetime: z.array(moneySchema),
  thisMonth: z.array(moneySchema),
  lastMonth: z.array(moneySchema),
  /** Average a paying organisation brings in a month, per currency. */
  arpa: z.array(moneySchema),
  /** Organisations that paid for the first time this month. */
  newThisMonth: z.number().int(),
  /** Paid plans that ran out this month without being paid again. */
  lapsedThisMonth: z.number().int(),
  /** The last twelve months, oldest first: settled money and how many payments. */
  months: z.array(
    z.object({
      month: z.string(), // 2026-10
      totals: z.array(moneySchema),
      payments: z.number().int(),
      failed: z.number().int(),
    }),
  ),
  byPlan: z.array(z.object({ plan: planIdSchema, totals: z.array(moneySchema) })),
  /** Payments that failed in the last 30 days, by the reason Paystack gave. */
  failureReasons: z.array(z.object({ reason: z.string(), count: z.number().int() })),
});
export type AdminRevenue = z.infer<typeof adminRevenueSchema>;

export const adminPaymentRowSchema = adminPaymentSchema.extend({
  orgId: z.string(),
  interval: billingIntervalSchema.nullable(),
  receipt: z.string().nullable(),
  settledAt: z.string().nullable(),
});
export type AdminPaymentRow = z.infer<typeof adminPaymentRowSchema>;
export const adminPaymentQuerySchema = z.object({
  q: search,
  status: optionalEnum(["pending", "succeeded", "failed", "refunded"] as const),
  kind: optionalEnum(["checkout", "renewal"] as const),
  currency: optionalEnum(["KES", "USD"] as const),
  /** Inclusive dates, YYYY-MM-DD, in UTC. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
  ...pagingSchema,
});
export type AdminPaymentQuery = z.infer<typeof adminPaymentQuerySchema>;
export const adminPaymentPageSchema = adminPageSchema(adminPaymentRowSchema).extend({
  /** Settled money of every payment matched, not only this page. */
  totals: z.array(moneySchema),
});

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export const adminUserSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  createdAt: z.string(),
  organisations: z.array(z.object({ orgId: z.string(), name: z.string(), role: z.string() })),
  /** The last time any of their sign-ins was used. */
  lastActiveAt: z.string().nullable(),
  /** Sign-ins still valid. */
  signedIn: z.number().int(),
  blocked: z.boolean(),
  /** On PLATFORM_ADMIN_USER_IDS. */
  platformAdmin: z.boolean(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;
export const adminUserQuerySchema = z.object({
  q: search,
  status: optionalEnum(["blocked", "unverified", "no_organisation", "admins"] as const),
  sort: z.enum(["newest", "oldest", "name", "active"] as const).catch("newest").default("newest"),
  ...pagingSchema,
});
export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>;
export const adminUserPageSchema = adminPageSchema(adminUserSchema);

export const adminAuditQuerySchema = z.object({
  action: optionalEnum(ADMIN_ACTIONS),
  ...pagingSchema,
});
export type AdminAuditQuery = z.infer<typeof adminAuditQuerySchema>;
export const adminAuditPageSchema = adminPageSchema(adminAuditEntrySchema);

export const adminUserDetailSchema = z.object({
  user: adminUserSchema,
  block: z.object({ reason: z.string(), blockedAt: z.string(), by: z.string().nullable() }).nullable(),
  /** Their sign-ins still valid, newest first. Never the token. */
  signIns: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      lastActiveAt: z.string(),
      expiresAt: z.string(),
      ipAddress: z.string().nullable(),
      userAgent: z.string().nullable(),
      organisation: z.string().nullable(),
    }),
  ),
  /** Invitations sent to their email, any organisation, still waiting. */
  invitations: z.array(z.object({ organisation: z.string(), role: z.string().nullable(), expiresAt: z.string() })),
  actions: z.array(adminAuditEntrySchema),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

// ---------------------------------------------------------------------------
// Sessions and the system
// ---------------------------------------------------------------------------

export const adminSessionRowSchema = z.object({
  sessionId: z.string(),
  orgId: z.string(),
  organisation: z.string(),
  deck: z.string().nullable(),
  joinCode: z.string(),
  mode: z.enum(["live", "self_paced"]),
  status: z.enum(["lobby", "live", "ended"]),
  participants: z.number().int(),
  answers: z.number().int(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  closesAt: z.string().nullable(),
});
export type AdminSessionRow = z.infer<typeof adminSessionRowSchema>;
export const adminSessionQuerySchema = z.object({
  /** An organisation's name, a deck's title, or a join code. */
  q: search,
  status: optionalEnum(["open", "ended"] as const),
  mode: optionalEnum(["live", "self_paced"] as const),
  orgId: z.string().max(100).optional().catch(undefined),
  ...pagingSchema,
});
export type AdminSessionQuery = z.infer<typeof adminSessionQuerySchema>;
export const adminSessionPageSchema = adminPageSchema(adminSessionRowSchema);

const queueSchema = z.object({
  name: z.string(),
  workers: z.number().int(),
  waiting: z.number().int(),
  active: z.number().int(),
  delayed: z.number().int(),
  failed: z.number().int(),
  completed: z.number().int(),
});

export const adminSystemSchema = z.object({
  generatedAt: z.string(),
  health: z.object({ database: z.boolean(), valkey: z.boolean() }),
  queues: z.array(queueSchema),
  /** The latest jobs that failed, any queue, newest first. */
  failedJobs: z.array(
    z.object({
      queue: z.string(),
      id: z.string(),
      name: z.string(),
      reason: z.string(),
      attempts: z.number().int(),
      failedAt: z.string().nullable(),
    }),
  ),
  database: z.object({
    sizeBytes: z.number(),
    /** The largest tables, by Postgres's own row estimate. */
    tables: z.array(z.object({ name: z.string(), rows: z.number(), bytes: z.number() })),
  }),
  /** How this deployment is set up, as the API sees it. No secrets. */
  config: z.object({
    environment: z.string(),
    billing: z.boolean(),
    currencies: z.array(z.string()),
    storage: z.string(),
    admins: z.number().int(),
  }),
  uptimeSeconds: z.number(),
});
export type AdminSystem = z.infer<typeof adminSystemSchema>;

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/** Why, in words the organisation will read on its suspended page. */
export const adminSuspendSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
export type AdminSuspendRequest = z.infer<typeof adminSuspendSchema>;

export const adminReinstateSchema = z.object({ note: z.string().trim().max(500).optional() }).strict();
export type AdminReinstateRequest = z.infer<typeof adminReinstateSchema>;

/**
 * Put an organisation on a plan by hand, without a payment: a comped month of Pro, an
 * Enterprise arrangement, back to Free. Starter, Pro and Business run for `days`;
 * Enterprise for `days` or, without them, until changed; Free never ends.
 */
const planGrantSchema = z.object({
  plan: planIdSchema,
  days: z.number().int().min(1).max(3650).optional(),
  /** Enterprise only: how many participants a session admits. */
  participantLimit: z.number().int().min(1).max(100_000).optional(),
});

/** A paid plan runs for some days; only Enterprise takes a participant limit, and needs one. */
function checkGrant(r: z.infer<typeof planGrantSchema>, ctx: z.RefinementCtx) {
  if (isPaidPlan(r.plan) && r.days === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "A paid plan needs a number of days", path: ["days"] });
  }
  if (r.plan === "enterprise" ? r.participantLimit === undefined : r.participantLimit !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Only Enterprise takes a participant limit, and it needs one", path: ["participantLimit"] });
  }
}

export const adminSetPlanSchema = planGrantSchema
  .extend({ note: z.string().trim().max(500).optional() })
  .strict()
  .superRefine(checkGrant);
export type AdminSetPlanRequest = z.infer<typeof adminSetPlanSchema>;

/** Keep the plan, move its end: a goodwill week, time to sort out a payment. */
export const adminExtendSchema = z
  .object({ days: z.number().int().min(1).max(3650), note: z.string().trim().max(500).optional() })
  .strict();
export type AdminExtendRequest = z.infer<typeof adminExtendSchema>;

/** Stop charging the saved card (or resume, where there is one). */
export const adminAutoRenewSchema = z.object({ enabled: z.boolean() }).strict();
export type AdminAutoRenewRequest = z.infer<typeof adminAutoRenewSchema>;

export const adminEndSessionSchema = z.object({ reason: z.string().trim().max(500).optional() }).strict();
export type AdminEndSessionRequest = z.infer<typeof adminEndSessionSchema>;

/** Why, kept with the block and in the audit log. They are told only that they are blocked. */
export const adminBlockSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
export type AdminBlockRequest = z.infer<typeof adminBlockSchema>;

export const adminNoteSchema = z.object({ note: z.string().trim().max(500).optional() }).strict();
export type AdminNoteRequest = z.infer<typeof adminNoteSchema>;

// ---------- prices ----------

/**
 * One price as it stands, and who pays it: the subscriptions on that plan, period and
 * currency whose time has not run out. Each pays the new price the next time they pay,
 * by card renewal or by paying again.
 */
export const adminPriceRowSchema = z.object({
  plan: paidPlanSchema,
  interval: billingIntervalSchema,
  currency: currencySchema,
  amountMinor: z.number().int(),
  subscribers: z.number().int(),
  /** Of those, the ones a saved card renews by itself. */
  renewing: z.number().int(),
});
export type AdminPriceRow = z.infer<typeof adminPriceRowSchema>;

export const adminPricesSchema = z.object({
  rows: z.array(adminPriceRowSchema),
  /** The last change, from the audit log; null while the prices are the ones it started with. */
  updated: z.object({ at: z.string(), by: z.string().nullable() }).nullable(),
});
export type AdminPrices = z.infer<typeof adminPricesSchema>;

/** The whole price list, every price in it, as it should be from now on. */
export const adminSetPricesSchema = z.object({ prices: priceListSchema, note: z.string().trim().max(500).optional() }).strict();
export type AdminSetPricesRequest = z.infer<typeof adminSetPricesSchema>;

// ---------- creating people ----------

/** What an organisation member can be made. Only owners, admins and members exist (Better Auth). */
export const MEMBER_ROLES = ["owner", "admin", "member"] as const;
export const memberRoleSchema = z.enum(MEMBER_ROLES);
export type MemberRole = z.infer<typeof memberRoleSchema>;

/**
 * Make an account for someone, and the link they set their own password with (ADR 0009).
 * They land either in a new organisation of their own, which they own — on a plan given
 * by hand if `plan` says so — or in one that exists, in the role given.
 */
export const adminCreateUserSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    email: z.string().trim().toLowerCase().email().max(254),
    organisation: z.discriminatedUnion("kind", [
      z
        .object({
          kind: z.literal("new"),
          name: z.string().trim().min(1).max(100),
          plan: planGrantSchema.strict().superRefine(checkGrant).optional(),
        })
        .strict(),
      z.object({ kind: z.literal("existing"), orgId: z.string().min(1).max(100), role: memberRoleSchema }).strict(),
    ]),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type AdminCreateUserRequest = z.infer<typeof adminCreateUserSchema>;

/**
 * A set-password link's secret, for the web app to put in its own address. Shown to the
 * admin once; it works once, until `expiresAt`.
 */
export const adminPasswordLinkSchema = z.object({ token: z.string(), expiresAt: z.string() });
export type AdminPasswordLink = z.infer<typeof adminPasswordLinkSchema>;

export const adminCreatedUserSchema = adminPasswordLinkSchema.extend({ userId: z.string(), orgId: z.string() });
export type AdminCreatedUser = z.infer<typeof adminCreatedUserSchema>;
